import type { CampaignLinkRecord } from '../types/emailLinkAnalytics';
import { buildCampaignLinkRegistry } from './emailLinkRegistry';
import { applyCampaignTrackingToHtml } from '../services/secureEmailService';
import { campaignLinkService } from '../services/campaignLinkService';

export interface AutomationLinkTrackingContext {
  workspaceId: string;
  automationId: string;
  actionId: string;
  automationName?: string;
  recipientEmail: string;
  recipientId?: string;
  designJson?: unknown;
  htmlContent: string;
  openTracking?: boolean;
  clickTracking?: boolean;
}

/** Stable Firestore campaignId for link_stats / campaign_links (automation step). */
export function buildAutomationTrackingCampaignId(automationId: string, actionId: string): string {
  const safeAuto = String(automationId).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 80);
  const safeAction = String(actionId).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
  return `auto_${safeAuto}_${safeAction}`;
}

export function isAutomationTrackingCampaignId(campaignId: string): boolean {
  return String(campaignId || '').startsWith('auto_');
}

/** Same logical link keeps the same linkId across automation sends. */
export function generateStableAutomationLinkId(
  trackingCampaignId: string,
  link: Pick<CampaignLinkRecord, 'blockId' | 'sortOrder' | 'destinationUrl'>
): string {
  const key = `${trackingCampaignId}:${link.blockId ?? link.sortOrder}:${(link.destinationUrl || '').trim()}`;
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return `lnk_${(h >>> 0).toString(36)}`;
}

export function buildAutomationLinkRegistry(params: {
  workspaceId: string;
  automationId: string;
  actionId: string;
  automationName?: string;
  designJson?: unknown;
  htmlContent: string;
}): CampaignLinkRecord[] {
  const trackingCampaignId = buildAutomationTrackingCampaignId(params.automationId, params.actionId);
  const raw = buildCampaignLinkRegistry({
    designJson: params.designJson,
    htmlContent: params.htmlContent,
    workspaceId: params.workspaceId,
    campaignId: trackingCampaignId,
    campaignName: params.automationName || params.automationId,
  });

  return raw.map((link) => ({
    ...link,
    sourceType: 'automation',
    automationId: params.automationId,
    linkId: generateStableAutomationLinkId(trackingCampaignId, link),
  }));
}

/**
 * Register trackable links and wrap anchors / open pixel (parity with campaign send).
 */
export async function prepareAutomationEmailHtml(
  html: string,
  ctx: AutomationLinkTrackingContext
): Promise<string> {
  if (!html?.trim()) return html;

  const clickEnabled = ctx.clickTracking !== false;
  const openEnabled = ctx.openTracking !== false;
  if (!clickEnabled && !openEnabled) return html;

  const trackingCampaignId = buildAutomationTrackingCampaignId(ctx.automationId, ctx.actionId);
  let linkRegistry: CampaignLinkRecord[] = [];

  if (clickEnabled) {
    try {
      linkRegistry = buildAutomationLinkRegistry({
        workspaceId: ctx.workspaceId,
        automationId: ctx.automationId,
        actionId: ctx.actionId,
        automationName: ctx.automationName,
        designJson: ctx.designJson,
        htmlContent: ctx.htmlContent || html,
      });
      if (linkRegistry.length > 0) {
        await campaignLinkService.saveCampaignLinks(linkRegistry);
      }
    } catch (err) {
      console.error('Failed to save automation link registry (continuing send):', err);
    }
  }

  return applyCampaignTrackingToHtml(html, {
    trackingEnabled: true,
    openTracking: openEnabled,
    clickTracking: clickEnabled,
    campaignId: trackingCampaignId,
    workspaceId: ctx.workspaceId,
    recipientEmail: ctx.recipientEmail,
    recipientId: ctx.recipientId,
    linkRegistry,
  });
}
